import React from "react";
import MetricSummaryTile from "../../../../ui-components/metric-summary-tile/metricSummaryTile";
import {
  PickupStatsAnalytics,
  PickupStatsSummary,
} from "../../../../app/data/pickup-requests/models";
import {
  formatCount,
  formatHours,
  formatPercent,
  getPeriodDayCount,
} from "../utils/pickupStatsFormatters";

export interface PickupInboxAnalyticsSummaryProps {
  summary?: PickupStatsSummary;
  analytics?: PickupStatsAnalytics;
  isLoading: boolean;
}

const PickupInboxAnalyticsSummary: React.FC<PickupInboxAnalyticsSummaryProps> = ({
  summary,
  analytics,
  isLoading,
}) => {
  const period = analytics?.period ?? summary?.period;
  const outcomes = summary?.outcomes;
  const derived = summary?.derived;
  const reviewSla = analytics?.reviewSla;
  const dayCount = getPeriodDayCount(period?.from, period?.to);

  return (
    <div className="xgs-pickup-inbox-analytics__summary">
      <div className="xgs-pickup-inbox-analytics__summary__row">
        <div className="xgs-pickup-inbox-analytics__summary__row__title">Summary</div>
        <div className="xgs-pickup-inbox-analytics__summary__row__tiles">
          <MetricSummaryTile
            iconPreset="calendarBlue"
            colorVariant="blue"
            isLoading={isLoading}
            title="Days selected"
            value={dayCount || "—"}
            legendDescription="Number of days in the selected date range."
          />
          <MetricSummaryTile
            iconPreset="checkBlueCircle"
            colorVariant="blue"
            isLoading={isLoading}
            title="Automation rate"
            value={derived ? formatPercent(derived.automationRate) : "—"}
            legendDescription="Share of resolved pickups auto-created in the selected period."
          />
          <MetricSummaryTile
            iconPreset="clockYellow"
            colorVariant="yellow"
            isLoading={isLoading}
            title="Median time to review"
            value={formatHours(reviewSla?.medianTimeToReviewHours)}
            legendDescription="Median hours from receipt to human review decision."
          />
          <MetricSummaryTile
            iconPreset="calendarBlue"
            colorVariant="blue"
            isLoading={isLoading}
            title="P95 review time"
            value={formatHours(reviewSla?.p95TimeToReviewHours)}
            legendDescription="95th percentile hours to complete human review."
          />
        </div>
      </div>

      <div className="xgs-pickup-inbox-analytics__summary__row">
        <div className="xgs-pickup-inbox-analytics__summary__row__title">Outcomes</div>
        <div className="xgs-pickup-inbox-analytics__summary__row__tiles">
          <MetricSummaryTile
            iconPreset="fileBlue"
            colorVariant="blue"
            isLoading={isLoading}
            title="Received"
            value={outcomes ? formatCount(outcomes.received) : 0}
            legendDescription="Inbound emails received in the selected period."
          />
          <MetricSummaryTile
            iconPreset="fileGreenCheck"
            colorVariant="green"
            isLoading={isLoading}
            title="Auto-created"
            value={outcomes ? formatCount(outcomes.autoCreated) : 0}
            legendDescription="Pickups auto-created by the system in the selected period."
          />
          <MetricSummaryTile
            iconPreset="fileGreenCheck"
            colorVariant="green"
            isLoading={isLoading}
            title="Employee approved"
            value={outcomes ? formatCount(outcomes.approvedByHuman) : 0}
            legendDescription="Pickups approved by an employee in the selected period."
          />
          <MetricSummaryTile
            iconPreset="exclamationRed"
            colorVariant="red"
            isLoading={isLoading}
            title="Employee rejected"
            value={outcomes ? formatCount(outcomes.rejectedByHuman) : 0}
            legendDescription="Pickups rejected by an employee in the selected period."
          />
        </div>
      </div>
    </div>
  );
};

export default PickupInboxAnalyticsSummary;
